import './patient-modal.scss';

import { useMutation } from '@apollo/client';
import { kebabCase } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import { useHealthProviderPostalCodeItems } from '../../../../data/data/health-providers/use-health-provider-postal-code-items';
import { usePatientAppointmentsCheck } from '../../../../data/data/patients/use-patient-appointments-check';
import { usePatientItem } from '../../../../data/data/patients/use-patient-item';
import { useReminderItems } from '../../../../data/data/reminders/use-reminder-items';
import { useLanguageValues } from '../../../../data/data/select-values';
import {
  AgendaAppointmentItemDataFragment,
  BaseReminderItemDataFragment,
  Gender,
  GetPatientItemsDocument,
  Language,
  MfaAuthenticationType,
  PatientItemDataFragment,
  PatientStatus,
  ReminderFrequencyGroup,
  UpdatePatientItemDocument,
} from '../../../../data/types/api/graphql';
import { formatDate, maxBirthDate } from '../../../../data/utils/date.utils';
import { FormError } from '../../../../data/utils/form.utils';
import {
  isEmptyOrNone,
  useFormatters,
} from '../../../../data/utils/formatters.utils';
import { useNavigateWithHealthProvider } from '../../../../data/utils/router.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import { AppointmentExceptionList } from '../../../../elements/appointment-exception-list/appointment-exception-list';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { InputCheckboxController } from '../../../../elements/form/controllers/input-checkbox';
import { InputFieldController } from '../../../../elements/form/controllers/input-field';
import { InputSelectController } from '../../../../elements/form/controllers/input-select';
import { FormRow } from '../../../../elements/form/form';
import { BaseInputIcon } from '../../../../elements/form/input/base-input';
import { InputCheckboxType } from '../../../../elements/form/input/input-checkbox';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Message, MessageType } from '../../../../elements/message/message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { PatientRemindersList } from '../../../../elements/patient-reminders-list/patient-reminders-list';
import { Tag, TagType } from '../../../../elements/tag/tag';
import { CopyText } from '../../../../elements/utils/copy-text/copy-text';
import { usePatientForm } from '../../forms/patient-form/use-patient-form';
import { PatientDeletePrompt } from '../patient-delete-prompt/patient-delete-prompt';
import { PatientResendWelcomeMailPrompt } from '../patient-resend-welcome-mail-prompt/patient-resend-welcome-mail-prompt';

export enum PatientModalAction {
  DEFAULT = 'DEFAULT',
  UPDATE = 'UPDATE',
}

type PatientModalProps = {
  action?: PatientModalAction;
  healthProviderId: string | null | undefined;
  patientId: string | null | undefined;
  onSaved?: (patient: PatientItemDataFragment) => void;
  onCancel?: () => void;
  onClose?: () => void;
};

export function PatientModal({
  healthProviderId,
  patientId,
  onSaved,
  onCancel,
  onClose,
  ...props
}: PatientModalProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const { data: patientItemData, loading: patientItemLoading } =
    usePatientItem(patientId);

  // appointments
  const {
    data: patientAppointmentsCheckData,
    loading: patientAppointmentsCheckLoading,
  } = usePatientAppointmentsCheck(patientId);

  // reminders
  const {
    data: patientRemindersCheckData,
    loading: patientRemindersCheckLoading,
  } = useReminderItems({
    healthProviderId,
    patientId,
    group: ReminderFrequencyGroup.AllActive,
    take: 1000,
  });

  const [action, setAction] = useState<PatientModalAction>(
    PatientModalAction.DEFAULT
  );

  const [invalidPostalCode, setInvalidPostalCode] = useState(false);

  useEffect(() => {
    setAction(props.action ?? PatientModalAction.DEFAULT);
  }, [props.action]);

  const { form, formIsValid, setErrors } = usePatientForm();

  const { data: healthProviderPostalCodeItemsData } =
    useHealthProviderPostalCodeItems({
      healthProviderId,
    });

  const checkPostalCode = useCallback(
    (value: string) => {
      setInvalidPostalCode(
        !healthProviderPostalCodeItemsData.some(c => value.startsWith(c))
      );
    },
    [healthProviderPostalCodeItemsData]
  );

  useEffect(() => {
    if (patientItemData) {
      const app =
        patientItemData.status === PatientStatus.Imported
          ? true
          : patientItemData.app ?? true;

      form.reset({ ...patientItemData, app });
      checkPostalCode(patientItemData.postalCode);
    }
  }, [patientItemData, form, checkPostalCode]);

  const { data: languageData } = useLanguageValues();

  const [updatePatientItem, { loading: updatePatientItemLoading }] =
    useMutation(UpdatePatientItemDocument, {
      refetchQueries: [GetPatientItemsDocument],
    });

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();

      try {
        const result = await updatePatientItem({
          variables: {
            ...values,
            id: patientId ?? '',
            healthProviderId: healthProviderId ?? '',
            firstName: values.firstName ?? '',
            lastName: values.lastName ?? '',
            email: values.email ?? '',
            phoneNumber: values.phoneNumber
              ? values.phoneNumber.replace(/\s/g, '')
              : '',
            postalCode: values.postalCode ?? '',
            app: values.app === true,
            dutchInsurance: values.dutchInsurance === true,
            noShowProtocol: values.noShowProtocol === true,
          },
        });

        if (result.data?.patientUpdate?.patient) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'patient.patient-update.success',
            }),
          });

          onSaved?.(result.data.patientUpdate?.patient);

          onClose?.();
        } else {
          setErrors(result.data?.patientUpdate?.errors);
          // TODO pascalcase type in use-patient-form so the error is viewed in the schema.
          (result.data?.patientUpdate?.errors as FormError[]).forEach(e => {
            showAlertMessage({
              type: AlertMessageType.ERROR,
              message: intl.formatMessage({
                id: `common.errors.${kebabCase(e.message)}`,
                defaultMessage: e.message,
              }),
            });
          });
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    intl,
    form,
    patientId,
    healthProviderId,
    updatePatientItem,
    showAlertMessage,
    setErrors,
    onSaved,
    onClose,
  ]);

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [resendWelcomeMailModalOpen, setResendWelcomeMailModalOpen] =
    useState<boolean>(false);

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (action === PatientModalAction.UPDATE) {
        if (!confirmed && form.formState.isDirty) {
          setCancelPromptOpen(true);
        } else {
          setAction(PatientModalAction.DEFAULT);
        }
      } else {
        onCancel?.();

        onClose?.();
      }
    },
    [form.formState.isDirty, action, onCancel, onClose]
  );

  const navigate = useNavigateWithHealthProvider();

  const onHandleAppointmentException = useCallback(
    (appointment: AgendaAppointmentItemDataFragment) => {
      onCancel?.();

      onClose?.();

      navigate(
        `/agenda/${appointment.healthProfessional?.id}/${formatDate(
          appointment.startTime
        )}`
      );
    },
    [navigate, onCancel, onClose]
  );

  const onHandleReminder = useCallback(
    (reminder: BaseReminderItemDataFragment) => {
      onCancel?.();

      onClose?.();

      navigate(`/reminders/${kebabCase(reminder.frequency)}`);
    },
    [navigate, onCancel, onClose]
  );

  const { getFullName, getBirthDate } = useFormatters();

  const appUser = form.watch('app') === true;

  return (
    <>
      <Modal
        testId="patient"
        loading={patientItemLoading}
        title={
          action !== PatientModalAction.DEFAULT ? (
            <CustomFormattedMessage
              id={`patient.patient-${kebabCase(action)}.title`}
            />
          ) : (
            <CustomFormattedMessage id="patient.patient.title" />
          )
        }
        width={ModalWidth.LARGE}
        onClose={onClose}
        buttons={
          action === PatientModalAction.UPDATE ? (
            <>
              <Button
                type={ButtonType.SECONDARY}
                inverted
                onClick={() => onHandleCancel(false)}
              >
                <CustomFormattedMessage id="common.cancel" />
              </Button>

              <Button
                icon={
                  updatePatientItemLoading
                    ? ButtonIcon.LOADING
                    : ButtonIcon.NONE
                }
                disabled={
                  (patientItemData.status !== PatientStatus.Imported &&
                    !formIsValid) ||
                  updatePatientItemLoading
                }
                onClick={onHandleSubmit}
              >
                <CustomFormattedMessage
                  id={`patient.patient-update.${
                    patientItemData.status === PatientStatus.Imported
                      ? 'activate'
                      : 'save'
                  }`}
                />
              </Button>
            </>
          ) : (
            <>
              <Button
                disabled={
                  patientAppointmentsCheckLoading ||
                  patientAppointmentsCheckData.hasAppointments
                }
                inverted
                onClick={() => setDeleteModalOpen(true)}
              >
                <CustomFormattedMessage id="patient.button.delete" />
              </Button>

              <Button onClick={() => setAction(PatientModalAction.UPDATE)}>
                <CustomFormattedMessage id="patient.button.update" />
              </Button>
            </>
          )
        }
      >
        {action === PatientModalAction.UPDATE ? (
          <FormProvider {...form}>
            <FormRow>
              <InputFieldController
                name="firstName"
                label={
                  <>
                    <CustomFormattedMessage id="common.firstname" />
                    <small>*</small>
                  </>
                }
                placeholder={intl.formatMessage({ id: 'common.firstname' })}
              />
            </FormRow>

            <FormRow>
              <InputFieldController
                name="lastName"
                label={
                  <>
                    <CustomFormattedMessage id="common.lastname" />
                    <small>*</small>
                  </>
                }
                placeholder={intl.formatMessage({ id: 'common.lastname' })}
              />
            </FormRow>

            <FormRow>
              <InputFieldController
                icon={BaseInputIcon.DATE}
                type="date"
                name="birthDate"
                label={
                  <>
                    <CustomFormattedMessage id="common.birthdate" />
                    <small>*</small>
                  </>
                }
                max={maxBirthDate}
              />
            </FormRow>

            <FormRow>
              <InputCheckboxController<Gender>
                type={InputCheckboxType.RADIO}
                name="gender"
                label={<CustomFormattedMessage id="common.gender" />}
                options={[
                  {
                    key: Gender.Male,
                    title: <CustomFormattedMessage id="common.gender.male" />,
                  },
                  {
                    key: Gender.Female,
                    title: <CustomFormattedMessage id="common.gender.female" />,
                  },
                  {
                    key: Gender.Other,
                    title: <CustomFormattedMessage id="common.gender.other" />,
                  },
                ]}
              />
            </FormRow>

            <FormRow>
              <InputCheckboxController<boolean>
                name="app"
                label={<CustomFormattedMessage id="patient.app-user" />}
                options={[
                  {
                    key: true,
                    title: (
                      <CustomFormattedMessage id="patient.app-user-placeholder" />
                    ),
                  },
                ]}
              />
            </FormRow>

            <FormRow>
              <InputSelectController<Language>
                name="language"
                label={
                  <>
                    <CustomFormattedMessage id="common.language" />
                    <small>*</small>
                  </>
                }
                options={languageData}
              />
            </FormRow>
            <FormRow>
              <InputCheckboxController<MfaAuthenticationType>
                type={InputCheckboxType.RADIO}
                name="mfaAuthenticationType"
                label={
                  <>
                    <CustomFormattedMessage id="common.mfa-authentication-type" />{' '}
                    <small>*</small>
                  </>
                }
                options={[
                  {
                    key: MfaAuthenticationType.Sms,
                    title: (
                      <CustomFormattedMessage id="common.mfa-authentication-type.sms" />
                    ),
                  },
                  {
                    key: MfaAuthenticationType.Email,
                    title: (
                      <CustomFormattedMessage id="common.mfa-authentication-type.email" />
                    ),
                  },
                ]}
              />
            </FormRow>
            <FormRow>
              <InputFieldController
                type="phone"
                name="phoneNumber"
                label={
                  <>
                    <CustomFormattedMessage id="common.phone" />
                    {appUser && <small>*</small>}
                  </>
                }
                placeholder={intl.formatMessage({
                  id: 'common.phone',
                })}
              />
            </FormRow>

            <FormRow>
              <InputFieldController
                name="email"
                label={
                  <>
                    <CustomFormattedMessage id="common.email" />
                    {appUser && <small>*</small>}
                  </>
                }
                placeholder={intl.formatMessage({ id: 'common.email' })}
              />
            </FormRow>

            <FormRow>
              <InputFieldController
                name="postalCode"
                label={
                  <>
                    <CustomFormattedMessage id="common.postalcode" />
                    <small>*</small>
                  </>
                }
                placeholder={intl.formatMessage({
                  id: 'common.postalcode',
                })}
                onChange={() => {
                  setInvalidPostalCode(false);
                }}
                onBlur={checkPostalCode}
              />
              {invalidPostalCode && (
                <Message
                  type={MessageType.WARNING}
                  message={
                    <CustomFormattedMessage id="patient.postalcode.warning" />
                  }
                />
              )}
            </FormRow>

            <FormRow>
              <InputCheckboxController<boolean>
                name="dutchInsurance"
                label={<CustomFormattedMessage id="patient.insurance" />}
                options={[
                  {
                    key: true,
                    title: (
                      <CustomFormattedMessage id="patient.insurance-placeholder" />
                    ),
                  },
                ]}
              />
            </FormRow>

            <FormRow>
              <InputCheckboxController<boolean>
                name="noShowProtocol"
                label={<CustomFormattedMessage id="patient.no-show-protocol" />}
                options={[
                  {
                    key: true,
                    title: (
                      <CustomFormattedMessage id="patient.no-show-protocol-placeholder" />
                    ),
                  },
                ]}
              />
            </FormRow>
          </FormProvider>
        ) : (
          <div className="patient-modal-info">
            <div className="patient-modal-info__content">
              <h3>{getFullName(patientItemData)}</h3>

              <h4>
                <CustomFormattedMessage id="common.birthdate" />
              </h4>
              <p>
                {!isEmptyOrNone(patientItemData?.birthDate) ? (
                  <CopyText
                    text={formatDate(patientItemData.birthDate, 'dd-mm-YYYY')}
                  >
                    {getBirthDate(patientItemData)}
                  </CopyText>
                ) : (
                  '-'
                )}
              </p>

              <h4>
                <CustomFormattedMessage id="common.phone" />
              </h4>
              <p>
                {!isEmptyOrNone(patientItemData?.phoneNumber)
                  ? patientItemData?.phoneNumber
                  : '-'}
              </p>

              <h4>
                <CustomFormattedMessage id="common.email" />
              </h4>
              <p>
                {patientItemData?.email &&
                !isEmptyOrNone(patientItemData?.email) ? (
                  <a
                    href={`mailto:${patientItemData?.email}`}
                    title={patientItemData?.email}
                  >
                    {patientItemData?.email}
                  </a>
                ) : (
                  '-'
                )}
              </p>

              <h4>
                <CustomFormattedMessage id="common.language" />
              </h4>
              <p>
                <CustomFormattedMessage
                  id={`common.language.${kebabCase(patientItemData?.language)}`}
                />
              </p>

              {patientAppointmentsCheckData.hasAppointments && (
                <>
                  <h4>
                    <CustomFormattedMessage
                      id="common.agenda-appointment.appointments-blocking"
                      values={{
                        count:
                          patientAppointmentsCheckData.appointments.length ?? 0,
                      }}
                    />
                  </h4>

                  <AppointmentExceptionList
                    items={patientAppointmentsCheckData.appointments}
                    includeHealthProfessional
                    onClick={onHandleAppointmentException}
                  />
                </>
              )}

              {patientRemindersCheckData?.items && (
                <>
                  <h4>
                    <CustomFormattedMessage
                      id="common.agenda-appointment.reminders"
                      values={{
                        count: patientRemindersCheckData.items.length ?? 0,
                      }}
                    />
                  </h4>

                  <PatientRemindersList
                    items={patientRemindersCheckData?.items}
                    loading={patientRemindersCheckLoading}
                    onClick={onHandleReminder}
                  />
                </>
              )}
            </div>
            <div className="patient-modal-info__tags">
              {!patientItemData?.dutchInsurance && (
                <Tag type={TagType.NOINSURANCE} />
              )}
              {patientItemData?.noShowProtocol && (
                <Tag type={TagType.NOSHOWPROTOCOL} />
              )}

              <Tag type={patientItemData?.status} />
              {patientItemData?.app &&
                patientItemData?.status === PatientStatus.Pending && (
                  <div>
                    <Button
                      type={ButtonType.TEXT}
                      onClick={() => setResendWelcomeMailModalOpen(true)}
                    >
                      <CustomFormattedMessage id="patient.button.resend-email" />
                    </Button>
                  </div>
                )}
            </div>
          </div>
        )}
      </Modal>

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}

      {deleteModalOpen && (
        <PatientDeletePrompt
          patientItem={patientItemData}
          onDeleted={() => {
            onClose?.();
          }}
          onClose={() => {
            setDeleteModalOpen(false);
          }}
        />
      )}

      {resendWelcomeMailModalOpen && (
        <PatientResendWelcomeMailPrompt
          patientItem={patientItemData}
          onClose={() => {
            setResendWelcomeMailModalOpen(false);
          }}
        />
      )}
    </>
  );
}
